import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Dashboard.module.css';
import { getAuthToken } from '../../lib/auth';
import { HOST } from '../../config/api';
import { getExerciseCategoryLabel } from '../../utils/exerciseCategoryLabels';

const CoachDashboard = ({ userId, userInfo, demoData }) => {
    const [trainingPlans, setTrainingPlans] = useState([]);
    const [globalActivity, setGlobalActivity] = useState([]);
    const [selectedVariantId, setSelectedVariantId] = useState(demoData?.selectedVariantId || '');
    const [currentTraining, setCurrentTraining] = useState(null);
    const [isCurrentTrainingLoading, setIsCurrentTrainingLoading] = useState(false);
    const trainingRequestRef = useRef(0);

    useEffect(() => {
        if (demoData) {
            setTrainingPlans(demoData.trainingPlans || []);
            setGlobalActivity(demoData.activity || []);
            setCurrentTraining(demoData.currentTraining || null);
            return;
        }

        let active = true;

        const loadDashboard = async () => {
            try {
                const plansResponse = await fetch(`${HOST}/v1/training_plans?userid=${userId}`, {
                    headers: { Authorization: `Bearer ${getAuthToken()}` },
                });

                if (plansResponse.ok) {
                    const plansJson = await plansResponse.json();

                    if (active) {
                        const variants = plansJson?.variants || [];
                        setTrainingPlans(variants);
                        setSelectedVariantId((previousSelectedVariantId) => previousSelectedVariantId || String(variants[0]?.id || ''));
                    }
                } else {
                    console.error('Failed to load coach training plans', plansResponse.statusText);
                }
            } catch (error) {
                console.error('Failed to load coach training plans', error);
            }

            try {
                const globalHistoryResponse = await fetch(`${HOST}/v1/all_users_history?userid=${userId}`, {
                    headers: { Authorization: `Bearer ${getAuthToken()}` },
                });

                if (!globalHistoryResponse.ok) {
                    console.error('Failed to load coach global history', globalHistoryResponse.statusText);
                    return;
                }

                const globalHistoryJson = await globalHistoryResponse.json();

                if (active) {
                    const filteredGlobalHistory = (globalHistoryJson?.history || []).filter(
                        (item) => item.completedTrainingModels?.length > 0
                    );
                    setGlobalActivity(filteredGlobalHistory);
                }
            } catch (error) {
                console.error('Failed to load coach global history', error);
            }
        };

        loadDashboard();

        return () => {
            active = false;
        };
    }, [demoData, userId]);

    useEffect(() => {
        if (demoData) {
            return;
        }

        if (!selectedVariantId) {
            setCurrentTraining(null);
            setIsCurrentTrainingLoading(false);
            return;
        }

        let active = true;
        const requestId = ++trainingRequestRef.current;

        const loadTraining = async () => {
            setIsCurrentTrainingLoading(true);
            try {
                const response = await fetch(`${HOST}/v1/training?userid=${userId}&variant=${selectedVariantId}`, {
                    headers: { Authorization: `Bearer ${getAuthToken()}` },
                });
                const data = await response.json();

                if (active && requestId === trainingRequestRef.current) {
                    setCurrentTraining(data?.lastTraining || null);
                }
            } catch (error) {
                console.error('Failed to load current coach training', error);
            } finally {
                if (active && requestId === trainingRequestRef.current) {
                    setIsCurrentTrainingLoading(false);
                }
            }
        };

        loadTraining();

        return () => {
            active = false;
        };
    }, [demoData, userId, selectedVariantId]);

    const countRecordedExercises = (session) => (session?.exerciseHistoryModels || []).length;
    const hasTrainingPlans = trainingPlans.length > 0;

    const subscriberCount = userInfo?.subscriberCount || 0;
    const totalRecordedExercises = hasTrainingPlans
        ? globalActivity.reduce(
            (acc, item) =>
                acc +
                item.completedTrainingModels.reduce(
                    (trainingAcc, training) => trainingAcc + countRecordedExercises(training),
                    0
                ),
            0
        )
        : 0;

    const markVariantNotificationsRead = async (variantId) => {
        if (demoData || !variantId) {
            return;
        }

        setTrainingPlans((prevPlans) =>
            prevPlans.map((plan) =>
                String(plan.id) === String(variantId)
                    ? { ...plan, unreadCompletionCount: 0 }
                    : plan
            )
        );

        try {
            await fetch(`${HOST}/v1/mark_variant_notifications_read`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    variantId,
                }),
            });
        } catch (error) {
            console.error('Failed to mark variant notifications as read', error);
        }
    };

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div>
                    <span className={styles.kicker}>Coach summary</span>
                    <h2>{`Welcome back, ${userInfo?.firstName || 'Coach'}`}</h2>
                    <p>
                        {hasTrainingPlans
                            ? 'Start with your plan library, see what athletes completed recently, and jump straight into building the next training.'
                            : 'Start by creating your first training plan. Once you have a plan, you’ll be able to add trainings and share it with athletes.'}
                    </p>
                </div>

                <div className={styles.heroActions}>
                    <Link className={styles.primaryAction} to="/training_plans">
                        Create training plan
                    </Link>
                    {hasTrainingPlans ? (
                        <Link className={styles.secondaryAction} to="/trainings">
                            Add training
                        </Link>
                    ) : null}
                </div>
            </section>

            <section className={styles.statGrid}>
                <article className={styles.statCard}>
                    <span>Training plans</span>
                    <strong>{trainingPlans.length}</strong>
                    <small>Reusable plans ready to assign</small>
                </article>
                <article className={styles.statCard}>
                    <span>Subscribers</span>
                    <strong>{subscriberCount}</strong>
                    <small>Total athletes subscribed across all training plans</small>
                </article>
                <article className={styles.statCard}>
                    <span>Recorded exercises</span>
                    <strong>{totalRecordedExercises}</strong>
                    <small>Completed and failed exercises across all training plans</small>
                </article>
            </section>

            <section className={styles.grid}>
                <article className={`${styles.card} ${styles.cardLarge}`}>
                    <div className={styles.cardHeader}>
                        <div>
                            <h3>Your training plans</h3>
                            <p>Keep plans visible and make plan creation the primary coach action.</p>
                        </div>
                        <Link className={styles.inlineLink} to="/training_plans">
                            Manage plans
                        </Link>
                    </div>

                    <div className={styles.list}>
                        {trainingPlans.length === 0 ? (
                            <div className={styles.emptyState}>No plans yet. Start by creating your first training plan.</div>
                        ) : (
                            trainingPlans.slice(0, 5).map((plan) => (
                                <div key={plan.id} className={styles.listRow}>
                                    <div className={styles.listRowMeta}>
                                        <strong>{plan.name}</strong>
                                        <span>{plan.private ? 'Private share link' : 'Public subscription plan'}</span>
                                        {plan.unreadCompletionCount > 0 ? (
                                            <small className={styles.notificationBadge}>
                                                {plan.unreadCompletionCount} new completion{plan.unreadCompletionCount === 1 ? '' : 's'}
                                            </small>
                                        ) : null}
                                    </div>
                                    <Link
                                        className={styles.rowAction}
                                        to={`/trainings?variant=${plan.id}`}
                                        onClick={() => markVariantNotificationsRead(plan.id)}
                                    >
                                        Open
                                    </Link>
                                </div>
                            ))
                        )}
                    </div>
                </article>

                {hasTrainingPlans ? (
                    <article className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <h3>Current training</h3>
                                <p>Surface the selected plan’s latest session.</p>
                            </div>
                        </div>

                        <div className={styles.segmentedControl}>
                            {trainingPlans.slice(0, 4).map((plan) => (
                                <button
                                    key={plan.id}
                                    type="button"
                                    onClick={() => setSelectedVariantId(String(plan.id))}
                                    disabled={isCurrentTrainingLoading}
                                    className={`${styles.segmentButton} ${String(plan.id) === selectedVariantId ? styles.segmentButtonActive : ''}`}
                                >
                                    {plan.name}
                                </button>
                            ))}
                        </div>

                        <div className={styles.trainingSummary}>
                            <strong>{currentTraining?.name || 'Select a plan'}</strong>
                            {isCurrentTrainingLoading ? (
                                <div className={styles.inlineLoadingState} role="status" aria-live="polite">
                                    <div className={styles.inlineSpinner} />
                                    <span>Loading training…</span>
                                </div>
                            ) : null}
                            {currentTraining?.exercises?.length ? (
                                <div className={styles.trainingExerciseList}>
                                    {currentTraining.exercises.map((exercise, index) => (
                                        <div key={`${exercise.category}-${index}`} className={styles.trainingExerciseRow}>
                                            <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                            <span>{exercise.description || 'No description'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span>No training assigned to this plan yet.</span>
                            )}
                        </div>
                        <Link
                            className={`${styles.primaryAction} ${isCurrentTrainingLoading ? styles.disabledAction : ''}`}
                            to={isCurrentTrainingLoading ? '#' : '/trainings'}
                            aria-disabled={isCurrentTrainingLoading}
                            onClick={(event) => {
                                if (isCurrentTrainingLoading) {
                                    event.preventDefault();
                                }
                            }}
                        >
                            Build next training
                        </Link>
                    </article>
                ) : null}

            </section>
        </div>
    );
};

export default CoachDashboard;
